// A menu button (WAI-ARIA menu pattern): Enter, Space or a click opens it with focus on the first item;
// the arrow keys, Home and End move between items; Escape closes it and returns focus to the button;
// Tab, a click outside or focus moving elsewhere closes it.
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";

/** Calls `onDismiss` on a pointer press, or focus moving (Tab, `/`), outside `wrap` while `open`. */
export function useDismiss(open: boolean, wrap: RefObject<HTMLElement | null>, onDismiss: () => void) {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      if (event.target instanceof Node && !wrap.current?.contains(event.target)) dismiss.current();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", outside);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("focusin", outside);
    };
  }, [open, wrap]);
}

export interface MenuItem {
  readonly label: string;
  readonly onSelect: () => void;
  /** Set for a choice among several (menuitemradio): whether it is the current one. */
  readonly checked?: boolean;
  /** The item's language, when it differs from the page's. */
  readonly lang?: string;
  /** Set for a proper noun, such as the business's name, that no language translates. */
  readonly translate?: "no";
}

export function Menu({
  id,
  label,
  className,
  children,
  header,
  items,
}: {
  id: string;
  /** The button's accessible name. */
  label: string;
  className: string;
  /** What the button shows. */
  children: ReactNode;
  /** Shown above the items, outside the menu itself. */
  header?: ReactNode;
  items: readonly MenuItem[];
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const entries = () => [...(list.current?.querySelectorAll<HTMLElement>("[role^='menuitem']") ?? [])];
  useEffect(() => {
    if (open) entries()[0]?.focus();
  }, [open]);
  useDismiss(open, wrap, () => setOpen(false));

  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  const move = (event: KeyboardEvent) => {
    if (!open) return;
    const all = entries();
    const at = all.findIndex((e) => e === document.activeElement);
    const to =
      event.key === "ArrowDown"
        ? (at + 1) % all.length
        : event.key === "ArrowUp"
          ? (at - 1 + all.length) % all.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? all.length - 1
              : undefined;
    if (to !== undefined) {
      event.preventDefault();
      all[to]?.focus();
    } else if (event.key === "Escape") {
      event.stopPropagation();
      close();
    } else if (event.key === "Tab") setOpen(false);
  };

  return (
    <div ref={wrap} className="menu-wrap" onKeyDown={move}>
      <button
        ref={button}
        type="button"
        className={className}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
      >
        {children}
      </button>
      {open ? (
        <div className="menu">
          {header}
          <ul ref={list} id={id} role="menu" aria-label={label}>
            {items.map((item) => (
              <li role="none" key={item.label}>
                <button
                  type="button"
                  role={item.checked === undefined ? "menuitem" : "menuitemradio"}
                  aria-checked={item.checked}
                  lang={item.lang}
                  translate={item.translate}
                  tabIndex={-1}
                  onClick={() => {
                    close();
                    item.onSelect();
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
